import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { formatFranc } from '../lib/utils';
import { newsAPI } from '../lib/api';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Virtuoso } from 'react-virtuoso';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Newspaper, Clock, Zap, ExternalLink, Star, ChevronDown, ChevronUp, Megaphone, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import DeveloperNewsScreen from './DeveloperNewsScreen';
import { AffiliationDisclaimer } from '../components/LegalDisclaimer';

// PERF (2026-07-18 prod snappiness audit) — module-scoped session cache.
// Bottom tabs unmount on switch (App.js renderTabContent), so every visit
// used to remount NewsTab: skeleton flash + 2 fresh API round-trips
// (~0.5-1s perceived). Stale-while-revalidate: revisits render the cached
// list instantly and refresh silently in the background.
const _newsCache = { key: null, news: null, myNews: null };

// 2026-07-20 launch perf — react-virtuoso Footer (Load More lives inside the
// virtual scroller so it appears after the last real card).
const NewsListFooter = ({ context }) => {
  if (!context?.showMore) return <div className="h-1" />;
  return (
    <Button
      variant="outline"
      className="w-full"
      onClick={context.loadMoreNews}
      disabled={context.loadingMore}
      data-testid="news-load-more-button"
    >
      {context.loadingMore ? 'Loading…' : (
        <><ChevronDown className="h-4 w-4 mr-1" /> Load more news</>
      )}
    </Button>
  );
};
const NEWS_VIRTUOSO_COMPONENTS = { Footer: NewsListFooter };

export default function NewsTab({ selectedLeagueId, currentTeam, onEntityClick }) {
  const cacheKey = selectedLeagueId || '';
  const hasCache = _newsCache.key === cacheKey && Array.isArray(_newsCache.news);
  const [news, setNews] = useState(hasCache ? _newsCache.news : []);
  const [myNews, setMyNews] = useState(hasCache ? _newsCache.myNews : []);
  const [loading, setLoading] = useState(!hasCache);
  const [tab, setTab] = useState('world');  // 'world', 'my', 'developer'
  const [expandedId, setExpandedId] = useState(null);
  const [breakdowns, setBreakdowns] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreNews, setHasMoreNews] = useState(true);
  const [prevLeagueId, setPrevLeagueId] = useState(selectedLeagueId);

  // Auto-switch to "World" when league changes
  if (selectedLeagueId !== prevLeagueId) {
    setPrevLeagueId(selectedLeagueId);
    setTab('world');
  }

  // v5.7.89 — Reset the "Load More" availability when the tab switches.
  // Without this, exhausting the World list (hasMoreNews=false) would
  // also hide the Load More button on the My-Team tab even when My-Team
  // has more rows in the database. Re-evaluation happens via the actual
  // API response on the next Load More click.
  const [prevTab, setPrevTab] = useState(tab);
  if (tab !== prevTab) {
    setPrevTab(tab);
    setHasMoreNews(true);
  }

  // 2026-08-15 (owner request v2) — re-tapping the News bottom tab first
  // smooth-scrolls the CURRENT feed to the top, then flips to World once the
  // scroll settles ("nice reset feeling" instead of a teleport). Tab switches
  // still land on World instantly because this component remounts.
  useEffect(() => {
    const onRetap = (e) => {
      if (e?.detail?.tab !== 'news') return;
      const finish = () => setTab('world');
      if (window.scrollY <= 4) { finish(); return; }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      let done = false;
      const watch = () => {
        if (done) return;
        if (window.scrollY <= 4) { done = true; finish(); return; }
        requestAnimationFrame(watch);
      };
      requestAnimationFrame(watch);
      setTimeout(() => { if (!done) { done = true; finish(); } }, 1500);
    };
    window.addEventListener('fws:tab-retap', onRetap);
    return () => window.removeEventListener('fws:tab-retap', onRetap);
  }, []);

  const loadNews = useCallback(async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        newsAPI.list({ limit: 20 }),
        api.get('/news/my', { params: { league_id: selectedLeagueId || '' } }).catch(() => ({ data: { news: [] } }))
      ]);
      const nextNews = allRes.data.news || [];
      const nextMy = myRes.data.news || [];
      setNews(nextNews);
      setMyNews(nextMy);
      _newsCache.key = selectedLeagueId || '';
      _newsCache.news = nextNews;
      _newsCache.myNews = nextMy;
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedLeagueId]);

  // 2026-07 UX audit P1 — loadNews already re-creates when selectedLeagueId
  // changes; listing the id again as a dep double-fired the fetch pair on
  // every league switch (4 concurrent requests instead of 2).
  useEffect(() => { loadNews(); }, [loadNews]);

  // 2026-07 UX audit P1 — rapid double-taps on "Scoring Breakdown" fired
  // duplicate concurrent fetches (state doesn't populate until the first
  // response lands). Track in-flight ids in a ref.
  const pendingBreakdowns = useRef(new Set());

  const loadMoreNews = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      // v5.7.89 — Branch by which tab the user is on. Previously this
      // always hit `/news` (World) regardless of tab; clicking Load More
      // on the My-Team tab silently appended unrelated World rows into
      // the My-Team list. Now each tab paginates within its own filtered
      // set, and if My-Team returns zero new rows we surface "no more"
      // immediately.
      if (tab === 'my') {
        const res = await newsAPI.my({ limit: 20, skip: myNews.length, league_id: selectedLeagueId || '' });
        const moreNews = res.data.news || [];
        if (moreNews.length === 0) setHasMoreNews(false);
        else setMyNews(prev => {
          const seen = new Set(prev.map(x => x.id));
          return [...prev, ...moreNews.filter(x => x && !seen.has(x.id))];
        });
      } else {
        const res = await newsAPI.list({ limit: 20, skip: news.length });
        const moreNews = res.data.news || [];
        if (moreNews.length === 0) setHasMoreNews(false);
        else setNews(prev => {
          const seen = new Set(prev.map(x => x.id));
          return [...prev, ...moreNews.filter(x => x && !seen.has(x.id))];
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  // Filter news by search query (headline, entity names, source).
  // Memoized (2026-07 UX audit P2): after several "Load more" clicks this
  // filtered 100+ items synchronously on every render/keystroke.
  const displayNews = useMemo(() => {
    const items = tab === 'my' ? myNews : news;
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item =>
      (item.headline || '').toLowerCase().includes(q) ||
      (item.entity_names || []).some(n => n.toLowerCase().includes(q)) ||
      (item.source || '').toLowerCase().includes(q)
    );
  }, [searchQuery, tab, myNews, news]);

  // Developer news tab
  if (tab === 'developer') {
    return (
      <div className="p-4" data-testid="news-tab">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Newspaper className="h-5 w-5 shrink-0" />
          <h2 className="text-base font-semibold shrink-0" style={{ fontFamily: 'var(--font-heading)' }}>News</h2>
          <Tabs value={tab} onValueChange={setTab} className="ml-auto">
            <TabsList className="h-8">
              <TabsTrigger value="world" className="text-[10px] px-2">World</TabsTrigger>
              <TabsTrigger value="my" className="text-[10px] px-2 gap-0.5"><Star className="h-2.5 w-2.5" /> My Team</TabsTrigger>
              <TabsTrigger value="developer" className="text-[10px] px-2 gap-0.5" data-testid="news-tab-dev"><Megaphone className="h-2.5 w-2.5" /> Developer</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <DeveloperNewsScreen embedded={true} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 space-y-3" data-testid="news-tab">
        {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="news-tab">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Newspaper className="h-5 w-5 shrink-0" />
        <h2 className="text-base font-semibold shrink-0" style={{ fontFamily: 'var(--font-heading)' }}>News</h2>
        <div className="relative flex-1 min-w-[100px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            placeholder="Search headlines, people, sources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-xs"
            data-testid="news-search-input"
          />
        </div>
        <Tabs value={tab} onValueChange={setTab} className="shrink-0">
          <TabsList className="h-8">
            <TabsTrigger value="world" className="text-[10px] px-2" data-testid="news-tab-world">World</TabsTrigger>
            <TabsTrigger value="my" className="text-[10px] px-2 gap-0.5" data-testid="news-tab-my"><Star className="h-2.5 w-2.5" /> My Team</TabsTrigger>
            <TabsTrigger value="developer" className="text-[10px] px-2 gap-0.5" data-testid="news-tab-dev"><Megaphone className="h-2.5 w-2.5" /> Developer</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* 2026-07-20 launch perf — virtualized feed (react-virtuoso) */}
      <div className="h-[calc(100vh-240px)]" data-testid="news-feed">
        {displayNews.length === 0 ? (
          <div className="text-center py-12">
            <Newspaper className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground text-sm">
              {tab === 'my' ? 'No news for your team entities yet.' : 'No news yet.'}
            </p>
          </div>
        ) : (
        <Virtuoso
          style={{ height: '100%' }}
          data={displayNews}
          computeItemKey={(_, item) => item.id}
          increaseViewportBy={{ top: 200, bottom: 400 }}
          components={NEWS_VIRTUOSO_COMPONENTS}
          context={{ loadMoreNews, loadingMore, showMore: hasMoreNews }}
          itemContent={(_, item) => {
            const totalScored = Object.values(item.scored_points || {}).reduce((a, b) => a + b, 0);
            const hasScore = Object.keys(item.scored_points || {}).length > 0;
            const isExpanded = expandedId === item.id;

            const toggleBreakdown = async (e) => {
              e.stopPropagation();
              if (isExpanded) { setExpandedId(null); return; }
              setExpandedId(item.id);
              if (!breakdowns[item.id] && !pendingBreakdowns.current.has(item.id)) {
                pendingBreakdowns.current.add(item.id);
                try {
                  const res = await api.get(`/news/${item.id}/breakdown`);
                  setBreakdowns(prev => ({ ...prev, [item.id]: res.data }));
                } catch (er) {
                  // v5.7.101 — Per-item breakdown fetch can fail without
                  // breaking the news feed. Log so an endpoint regression
                  // shows up in the console.
                  console.warn(`[NewsTab] breakdown fetch failed for ${item.id}:`, er?.message || er);
                } finally {
                  pendingBreakdowns.current.delete(item.id);
                }
              }
            };

            return (
              <div className="pb-3">
              <div
                className="news-card bg-card border border-border/60 rounded-2xl p-4"
                data-testid="news-article"
              >
                <div className="flex items-start justify-between gap-3">
                  {/* Left — headline, badges, source link, timestamp */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-snug mb-2">{item.headline}</p>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {/* v5.7.59 — Prefer `entity_refs` (has id+name) so badges
                          deep-link to the EntityDetail page. Fallback to the
                          legacy `entity_names` array (text-only) for older
                          events that don't have ID resolution yet. */}
                      {item.entity_refs && item.entity_refs.length > 0 ? (
                        item.entity_refs.map((ref, i) => (
                          onEntityClick && ref.id ? (
                            <button
                              key={ref.id || i}
                              type="button"
                              className="text-[10px] px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                              onClick={(e) => { e.stopPropagation(); onEntityClick(ref.id); }}
                              data-testid={`news-entity-${ref.id}`}
                            >
                              {ref.name || 'Unknown'}
                            </button>
                          ) : (
                            <Badge key={i} variant="secondary" className="text-[10px] px-2 py-0.5">{ref.name || 'Unknown'}</Badge>
                          )
                        ))
                      ) : (
                        item.entity_names?.map((name, i) => (
                          <Badge key={i} variant="secondary" className="text-[10px] px-2 py-0.5">{name}</Badge>
                        ))
                      )}
                      {item.is_my_team && (
                        <Badge className="bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent-text))] text-[10px] px-2 py-0.5">
                          <Star className="h-2.5 w-2.5 mr-0.5" /> Your Team
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        {item.source_url ? (
                          <a href={item.source_url} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-1 text-primary hover:underline"
                             data-testid={`news-source-link-${item.id}`}
                             onClick={(e) => e.stopPropagation()}>
                            <ExternalLink className="h-3 w-3" /> {item.source}
                          </a>
                        ) : (
                          <><ExternalLink className="h-3 w-3" /> {item.source}</>
                        )}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {item.timestamp ? new Date(item.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Recent'}
                      </span>
                    </div>
                  </div>

                  {/* Right — score badge + clickable "Scoring Breakdown" toggle */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {hasScore && (
                      <Badge className="bg-[hsl(43,74%,52%)] text-[hsl(240,10%,10%)] text-[10px] px-2 py-1 flex items-center gap-1">
                        <Zap className="h-3 w-3" />+{totalScored}
                      </Badge>
                    )}
                    <button
                      type="button"
                      onClick={toggleBreakdown}
                      className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-primary hover:underline font-medium"
                      aria-expanded={isExpanded}
                      data-testid={`news-breakdown-toggle-${item.id}`}
                    >
                      Scoring Breakdown
                      {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>

                {/* Expanded breakdown — header removed (redundant with the toggle above) */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-border/40" data-testid={`news-breakdown-${item.id}`}>
                    {!breakdowns[item.id] ? (
                      <p className="text-xs text-muted-foreground italic">Loading breakdown…</p>
                    ) : (breakdowns[item.id].scores || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">
                        This article hasn't been scored yet. Either no entity in your roster was mentioned, or the AI didn't find a recognized scoring event.
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {breakdowns[item.id].scores.map((score, si) => {
                          const sId = score.entity_id;
                          const canClick = !!(onEntityClick && sId);
                          return (
                            <div
                              key={si}
                              className={`flex items-center justify-between text-xs px-2 py-1 rounded-lg bg-secondary/30 ${canClick ? 'cursor-pointer hover:bg-secondary/60 transition-colors' : ''}`}
                              onClick={canClick ? (e) => { e.stopPropagation(); onEntityClick(sId); } : undefined}
                              role={canClick ? 'button' : undefined}
                              tabIndex={canClick ? 0 : undefined}
                              data-testid={canClick ? `news-breakdown-entity-${sId}` : undefined}
                            >
                              <div className="min-w-0 flex-1">
                                <span className="font-medium">{score.entity_name || 'Unknown'}</span>
                                <span className="text-muted-foreground ml-2">{score.event_type?.replace(/_/g, ' ')}</span>
                              </div>
                              <span className="font-bold tabular-nums text-[hsl(var(--positive-text))] ml-2">
                                {formatFranc(score.total_points, { sign: true })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              </div>
            );
          }}
        />
        )}
      </div>
      <div className="mt-3 mb-1">
        <AffiliationDisclaimer testId="news-affiliation-disclaimer" />
      </div>
    </div>
  );
}
